import {useState} from 'react'
import {FormItemProps, Input, InputProps, Space} from 'antd'

import {
  Box,
  Button,
  Form,
  FormItem,
  UserPhoto,
  Select,
  useForm,
  useWatch,
} from 'common/components'
import {FIELD_RULES, EMAIL_FIELD_RULES} from 'common/constants'
import {FileInterface} from 'common/types'
import {
  
  useCreateUser,
  useUpdateUser,
  UserBase,
  User,
  ROLE_NAME,
} from '..'
import ManagerSelectField from './ManagerSelectField'

interface TeamMemberFormProps {
  user?: User
  onClose: () => void
}

const roleEditOptions = [
  {value: 'manager', label: ROLE_NAME.manager},
  {value: 'associated-manager', label: ROLE_NAME['associated-manager']},
  {value: 'coordinator', label: ROLE_NAME.coordinator},
  {value: 'controller', label: ROLE_NAME.controller},
]

const roleCreateOptions = roleEditOptions.slice(0, 2)

const Field = ({
  name,
  type,
  readOnly,
  ...props
}: FormItemProps & InputProps) => (
  <FormItem
    {...props}
    name={name}
    rules={type === 'email' ? EMAIL_FIELD_RULES : FIELD_RULES}
  >
    <Input
      type={type}
      autoFocus={name === 'firstName'}
      addonBefore={name === 'phone' ? '+64' : undefined}
      readOnly={readOnly}
    />
  </FormItem>
)

export default function TeamMemberForm({user, onClose}: TeamMemberFormProps) {
  const [image, setImage] = useState<FileInterface>(
    user?.photo ? {url: user.photo} : {}
  )
  const {mutate: createTeamMember, isMutating: isCreateMutating} =
    useCreateUser('team', onClose)
  const {mutate: updateTeamMember, isMutating: isUpdateMutating} =
    useUpdateUser(user?.uuid, onClose)
  const [form] = useForm()
  const roleValue = useWatch('role', form)

  const isEdit = Boolean(user)

  const handleFinish = ({role, email, ...values}: UserBase) => {
    const newUser = {
      ...values,
      phone: `+64${values.phone.trim()}`,
    }
    if (image.uuid) {
      newUser.photo = image.uuid
    }
    if (isEdit) {
      return updateTeamMember(newUser)
    }
    createTeamMember({...newUser, role, email})
  }

  const initialValues = isEdit
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        managedUsers: user.managedUsers?.map((user) => user.uuid),
        email: user.email,
        phone: user.phone.slice(3),
      }
    : {
        role: 'manager',
      }

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <UserPhoto image={image} setImage={setImage} />
      <Space size={16}>
        <Field name="firstName" label="First name" />
        <Field name="lastName" label="Last name" />
      </Space>
      <FormItem name="role" label="Role" rules={FIELD_RULES}>
        <Select
          readOnly={isEdit}
          options={isEdit ? roleEditOptions : roleCreateOptions}
        />
      </FormItem>
      {roleValue === 'associated-manager' && <ManagerSelectField />}
      <Field name="email" label="Email" type="email" readOnly={isEdit} />
      <Field name="phone" label="Phone number" />
      <Box mt={12}>
        <Button
          block
          htmlType="submit"
          loading={isCreateMutating || isUpdateMutating}
        >
          {isEdit ? 'Edit' : 'Save'} team member
        </Button>
      </Box>
    </Form>
  )
}
